import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { isAfter } from 'date-fns';
import { capitalize } from 'lodash-es';
import { Box, Column, extractPureTextFromRichText, RichTextViewer, Row, Typography } from 'seagull';
import '~/components/AgreementStudioPage/TemplateVariables/TemplateVariableBlot/TemplateVariableBlot';
import { useResponsive } from '~/client-flow/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { AgreementDataModel, serviceCostModel, serviceModel } from '~/models';
import { ServiceCostType } from '~/models/service-cost';
import { formatLongMonthDateWithLocalTimezone, utils } from '~/utils';
import { bulletPointsTextsPath, getServiceBulletPoints } from '~/utils/service-description/getServiceBulletPoints';
import { billingBadgesTextsPath, billingDetailsBadge, discountBadge as getDiscountBadge } from '~/utils/service-description/getServiceDescriptionBadges';
import { DesktopServiceItem } from './DesktopServiceItem';
import { MobileServiceItem } from './MobileServiceItem';
import styles from './ServiceItem.module.scss';
import { GetServiceItemTID } from './ServiceItem.types';
export const ServiceItem = (param)=>{
    let { service, relationship, now, isOptional, isSelected, onToggle, testId } = param;
    var _service_bundle;
    const { ServiceItemTestIds } = useMemo(()=>GetServiceItemTID(testId), [
        testId
    ]);
    const featureFlags = useFeatureFlags();
    const { isMobile } = useResponsive();
    const { t: billingBadgesTexts } = useLocaleKeys(billingBadgesTextsPath);
    const { t: bulletPointsTexts } = useLocaleKeys(bulletPointsTextsPath);
    const { t: calloutsTexts } = useLocaleKeys('billing.callouts');
    const { t: reviewTexts } = useLocaleKeys('clientFlow.reviewServices');
    const agreementData = useMemo(()=>AgreementDataModel.fromRelationship(relationship), [
        relationship
    ]);
    const discountBadgeData = getDiscountBadge(service, billingBadgesTexts);
    const getBulletPoints = (skin)=>getServiceBulletPoints({
            service,
            now,
            agreementData,
            isReadOnly: true,
            skin,
            variant: 'paragraph2',
            weight: 'light',
            featureFlags,
            texts: bulletPointsTexts
        });
    const getBillingBadgeText = ()=>billingDetailsBadge(service, {
            t: billingBadgesTexts,
            effectiveDate: agreementData.effectiveDate,
            isClientPreview: true,
            now
        }).text;
    const getFutureBillingCallout = ()=>{
        const startBillingAt = serviceModel.startBillingAt(service);
        if (!startBillingAt) return null;
        if (isAfter(startBillingAt, now)) {
            return calloutsTexts.futureBillingCallout({
                date: formatLongMonthDateWithLocalTimezone(startBillingAt)
            });
        }
        return null;
    };
    const futureBillingText = getFutureBillingCallout();
    const { CLIENT_ONBOARDING_NEW_FLOW } = featureFlags;
    const renderPrice = ()=>{
        switch(service.cost.pricingType){
            case ServiceCostType.Fixed:
            case ServiceCostType.Variable:
                var _service_cost_discount;
                return /*#__PURE__*/ _jsxs(Row, {
                    gap: 8,
                    alignItems: "baseline",
                    children: [
                        /*#__PURE__*/ _jsxs(Row, {
                            gap: 2,
                            alignItems: "baseline",
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "h6",
                                    skin: "standard",
                                    children: utils.formatDollarAmount(utils.applyDiscount(service.cost.price, service.cost.discount))
                                }),
                                serviceCostModel.isVariablePrice(service.cost) && /*#__PURE__*/ _jsxs(Typography, {
                                    variant: "paragraph2",
                                    skin: "secondary",
                                    children: [
                                        "/ ",
                                        capitalize(serviceCostModel.formattedUnitName(service.cost))
                                    ]
                                })
                            ]
                        }),
                        ((_service_cost_discount = service.cost.discount) === null || _service_cost_discount === void 0 ? void 0 : _service_cost_discount.amount()) && /*#__PURE__*/ _jsx(Typography, {
                            skin: "secondary",
                            textDecoration: "line-through",
                            children: utils.formatDollarAmount(service.cost.price)
                        })
                    ]
                });
            case ServiceCostType.Range:
                var _service_cost_discount1;
                return /*#__PURE__*/ _jsxs(Row, {
                    gap: 4,
                    alignItems: "baseline",
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            variant: "h6",
                            skin: "standard",
                            children: [
                                service.cost.minPrice && !service.cost.minPrice.eq(0) ? "".concat(utils.formatDollarAmount(utils.applyDiscount(service.cost.minPrice, service.cost.discount)), " - ") : reviewTexts.upTo(),
                                utils.formatDollarAmount(utils.applyDiscount(service.cost.maxPrice, service.cost.discount))
                            ]
                        }),
                        ((_service_cost_discount1 = service.cost.discount) === null || _service_cost_discount1 === void 0 ? void 0 : _service_cost_discount1.amount()) && /*#__PURE__*/ _jsxs(Typography, {
                            skin: "secondary",
                            textDecoration: "line-through",
                            children: [
                                !!service.cost.minPrice && "".concat(utils.formatDollarAmount(service.cost.minPrice), " - "),
                                utils.formatDollarAmount(service.cost.maxPrice)
                            ]
                        })
                    ]
                });
        }
    };
    const getSubtitlePrice = ()=>{
        switch(service.cost.pricingType){
            case ServiceCostType.Fixed:
            case ServiceCostType.Variable:
                return utils.formatDollarAmount(utils.applyDiscount(service.cost.price, service.cost.discount));
            case ServiceCostType.Range:
                {
                    const minFormatted = service.cost.minPrice && !service.cost.minPrice.eq(0) ? "".concat(utils.formatDollarAmount(utils.applyDiscount(service.cost.minPrice, service.cost.discount)), " - ") : reviewTexts.upTo();
                    return "".concat(minFormatted).concat(utils.formatDollarAmount(utils.applyDiscount(service.cost.maxPrice, service.cost.discount)));
                }
        }
    };
    const hasIncludedServices = ((_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services) && service.bundle.services.length > 0;
    const hasBillingDetails = getBulletPoints('secondary').bullets.length > 0;
    const hasDescription = !!service.description && extractPureTextFromRichText(service.description).trim().length > 0;
    const hasExpandableContent = hasDescription || hasIncludedServices || hasBillingDetails;
    const getServiceDescription = (options)=>{
        var _service_bundle;
        const { hideIncludedServices = false } = options !== null && options !== void 0 ? options : {};
        const bulletPoints = getBulletPoints('standard');
        return /*#__PURE__*/ _jsxs(Column, {
            gap: 16,
            children: [
                hasDescription && /*#__PURE__*/ _jsxs(Column, {
                    gap: 4,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            weight: "bold",
                            skin: "standard",
                            children: hasIncludedServices ? reviewTexts.bundleDetails() : reviewTexts.serviceDetails()
                        }),
                        /*#__PURE__*/ _jsx(RichTextViewer, {
                            value: service.description,
                            skin: "standard"
                        })
                    ]
                }),
                hasIncludedServices && !hideIncludedServices && /*#__PURE__*/ _jsxs(Column, {
                    gap: 4,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            weight: "bold",
                            skin: "standard",
                            children: reviewTexts.includedServicesLabel()
                        }),
                        /*#__PURE__*/ _jsx("ul", {
                            className: styles.detailsList,
                            children: (_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services.map((bundleService)=>/*#__PURE__*/ _jsx("li", {
                                    children: /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph2",
                                        skin: "standard",
                                        weight: "light",
                                        children: bundleService.name
                                    })
                                }, bundleService.serviceTemplateId))
                        })
                    ]
                }),
                hasBillingDetails && /*#__PURE__*/ _jsxs(Column, {
                    gap: 4,
                    testId: ServiceItemTestIds.BillingDetails(),
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            weight: "bold",
                            skin: "standard",
                            children: reviewTexts.billing()
                        }),
                        bulletPoints.bullets.map((bullet, i)=>typeof bullet === 'string' ? /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                skin: "standard",
                                weight: "light",
                                children: bullet
                            }, i) : /*#__PURE__*/ _jsx(Box, {
                                children: bullet
                            }, i))
                    ]
                })
            ]
        });
    };
    const sharedProps = {
        service,
        isOptional,
        isSelected,
        onToggle,
        discountBadgeData,
        getBillingBadgeText,
        futureBillingText,
        useInlineFutureBilling: CLIENT_ONBOARDING_NEW_FLOW,
        renderPrice,
        getSubtitlePrice,
        hasIncludedServices: !!hasIncludedServices,
        hasExpandableContent: !!hasExpandableContent,
        getServiceDescription,
        ServiceItemTestIds
    };
    if (isMobile) {
        return /*#__PURE__*/ _jsx(MobileServiceItem, _object_spread({}, sharedProps));
    }
    return /*#__PURE__*/ _jsx(DesktopServiceItem, _object_spread({}, sharedProps));
};
